import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';

/** Tooltip copy for Class B (heuristic / estimated) ML metrics — roadmap 4.2 */
export const ML_ESTIMATE_BASIS = {
  market_share:
    'Share of voice estimated from stored post engagement in your competitor set — not platform-reported market share.',
  quality_rubric:
    'Content quality score from our rubric (caption length, CTA, hashtags, media, readability) — not a platform score.',
  sentiment_nlp:
    'Sentiment estimated from competitor post captions via NLP or lexicon scoring — not platform-reported.',
  model_prediction:
    'Engagement prediction from our trained model on historical patterns — not platform-reported performance.',
  trend_heuristic:
    'Trend signal from rolling-window engagement analysis using configured thresholds.',
  rule_based:
    'Recommendation generated from rule-based analysis of stored competitor aggregates.',
  seo_rubric:
    'SEO scores and recommendations from website crawl rubrics — estimated, not from Google Search Console.',
  gap_projection:
    'Projected SEO score improvement from competitor gap heuristics — not a guaranteed outcome.',
  platform_engagement_rate:
    'Engagement rate computed from scraped post totals vs follower counts — not platform Insights API data.',
  pattern_analysis:
    'Patterns derived from aggregated competitor post structure and engagement — heuristic.',
  content_insights:
    'Content guidance generated from top-performing pattern statistics — heuristic recommendations.',
  best_time_heuristic:
    'General posting-time guidance when client-specific slot data is unavailable.',
  best_time_scheduling:
    'Suggested times from scheduling heuristics or top-performer analysis — estimated.',
  keyword_suggestion:
    'Based on industry heuristics, not live platform data. Volume/competition may come from Keyword Planner when configured.',
  generated_analysis:
    'AI-generated analysis — not sourced from platform data. Narratives and rubric sections are derived from stored metrics.',
  caption_edit_learning:
    'Suggestions derived from captions you edited before publish — preference learning, not platform Insights.',
  cta_heuristic:
    'Call-to-action tips from caption phrase matching — estimated guidance, not a platform rule.',
};

/** Short chip labels for Class B metrics — roadmap 4.2 / 2.4 */
export const ML_ESTIMATE_LABELS = {
  quality_rubric: 'Rubric',
  seo_rubric: 'Rubric',
  sentiment_nlp: 'NLP estimate',
  model_prediction: 'Estimated',
  market_share: 'Estimated',
  trend_heuristic: 'Estimated',
  rule_based: 'Estimated',
  gap_projection: 'Estimated',
  platform_engagement_rate: 'Estimated',
  pattern_analysis: 'Estimated',
  content_insights: 'Estimated',
  best_time_heuristic: 'Estimated',
  best_time_scheduling: 'Estimated',
  keyword_suggestion: 'Estimated',
  generated_analysis: 'AI-generated',
  low_sample: 'Low sample',
  caption_edit_learning: 'From your edits',
  cta_heuristic: 'Estimated',
};

/** Class A — factual scrape / stored public metrics (Phase 17.5). */
export const ML_CLASS_A_BASIS =
  'Fact from stored competitor scrape rows (public metrics) — not private platform Insights.';

export function MlClassABadge({
  label = 'Scrape data',
  tooltip,
  size = 'small',
  sx,
}) {
  return (
    <Tooltip title={tooltip || ML_CLASS_A_BASIS} arrow placement="top">
      <Chip
        label={label}
        size={size}
        variant="outlined"
        color="success"
        sx={{
          height: size === 'small' ? 22 : 26,
          fontSize: size === 'small' ? '0.7rem' : '0.75rem',
          fontWeight: 600,
          borderColor: 'rgba(63, 185, 80, 0.55)',
          color: '#3fb950',
          bgcolor: 'rgba(63, 185, 80, 0.08)',
          cursor: 'help',
          ...sx,
        }}
      />
    </Tooltip>
  );
}

export function MlEstimateBadge({
  basis,
  tooltip,
  label,
  size = 'small',
  sx,
}) {
  const resolvedLabel =
    label ?? (basis && ML_ESTIMATE_LABELS[basis]) ?? 'Estimated';
  const title =
    tooltip ||
    (basis && ML_ESTIMATE_BASIS[basis]) ||
    'Estimated from internal analysis — not platform-reported.';

  return (
    <Tooltip title={title} arrow placement="top">
      <Chip
        label={resolvedLabel}
        size={size}
        variant="outlined"
        color="warning"
        sx={{
          height: size === 'small' ? 22 : 26,
          fontSize: size === 'small' ? '0.7rem' : '0.75rem',
          fontWeight: 600,
          borderColor: 'rgba(210, 153, 34, 0.6)',
          color: '#d29922',
          bgcolor: 'rgba(210, 153, 34, 0.08)',
          cursor: 'help',
          ...sx,
        }}
      />
    </Tooltip>
  );
}

export function MlMetricLabel({ children, basis, tooltip, sx }) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        flexWrap: 'wrap',
        ...sx,
      }}
    >
      {children}
      <MlEstimateBadge basis={basis} tooltip={tooltip} />
    </Box>
  );
}

export function MlSectionTitle({
  children,
  basis,
  tooltip,
  variant = 'h6',
  gutterBottom = true,
  sx,
  ...rest
}) {
  return (
    <Typography
      variant={variant}
      gutterBottom={gutterBottom}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
        ...sx,
      }}
      {...rest}
    >
      {children}
      {(basis || tooltip) && (
        <MlEstimateBadge basis={basis} tooltip={tooltip} />
      )}
    </Typography>
  );
}

export default MlEstimateBadge;
